import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users, Bell, AlertTriangle, Megaphone, CheckCircle, RefreshCw, Send, Trash2 } from 'lucide-react';
import firstName from '@/utils/firstName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JefeDocentesDashboard = ({ user, onLogout }) => {
  const [profesores, setProfesores] = useState([]);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedProfesores, setSelectedProfesores] = useState([]);
  const [notifType, setNotifType] = useState(null);
  const [citatorioForm, setCitatorioForm] = useState({ fecha: '', hora: '', lugar: '', motivo: '' });
  const [avisoForm, setAvisoForm] = useState({ mensaje: '' });
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [profRes, notifRes] = await Promise.all([
        axios.get(`${API}/jefe-docentes/profesores`, { headers }),
        axios.get(`${API}/jefe-docentes/notificaciones`, { headers }),
      ]);
      setProfesores(profRes.data);
      setSentNotifications(notifRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleProf = (id) => setSelectedProfesores(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  const toggleAllProf = () => setSelectedProfesores(prev => prev.length === profesores.length ? [] : profesores.map(p => p.id));

  const sendCitatorio = async () => {
    if (!citatorioForm.fecha || !citatorioForm.hora || !citatorioForm.lugar || !citatorioForm.motivo) { toast.error('Todos los campos son requeridos'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/jefe-docentes/notificaciones/citatorio`, { teacher_ids: selectedProfesores, ...citatorioForm }, { headers });
      toast.success('Citatorio enviado');
      setNotifType(null); setCitatorioForm({ fecha: '', hora: '', lugar: '', motivo: '' }); setSelectedProfesores([]);
      fetchData();
    } catch (e) { toast.error('Error al enviar'); }
    finally { setSending(false); }
  };

  const sendAviso = async () => {
    if (!avisoForm.mensaje) { toast.error('Escribe un mensaje'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/jefe-docentes/notificaciones/aviso`, { teacher_ids: selectedProfesores, ...avisoForm }, { headers });
      toast.success('Aviso enviado');
      setNotifType(null); setAvisoForm({ mensaje: '' }); setSelectedProfesores([]);
      fetchData();
    } catch (e) { toast.error('Error al enviar'); }
    finally { setSending(false); }
  };

  const deleteNotif = async (id) => {
    try { await axios.delete(`${API}/jefe-docentes/notificaciones/${id}`, { headers }); toast.success('Eliminada'); fetchData(); }
    catch (e) { toast.error('Error'); }
  };

  if (loading) return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-indigo-500" /></div>
    </Layout>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            {firstName(user?.name) && (
              <p className="text-sm text-slate-500 mb-0.5" data-testid="welcome-greeting">
                Bienvenido(a), <span className="font-semibold text-slate-700">{firstName(user?.name)}</span>
              </p>
            )}
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1" data-testid="jefe-dashboard-title">Panel de Jefe de Docentes</h1>
            <p className="text-sm text-slate-600">Citatorios y avisos a profesores</p>
          </div>
          <button
            onClick={() => { window.location.href = '/horarios'; }}
            data-testid="jefe-horarios-btn"
            className="inline-flex items-center gap-1 text-sm px-3 h-9 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
          >
            <Users className="w-4 h-4" /> Horarios
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="p-4 text-center"><div className="text-3xl font-bold text-indigo-600">{profesores.length}</div><div className="text-sm text-slate-600">Profesores</div></Card>
          <Card className="p-4 text-center"><div className="text-3xl font-bold text-amber-600">{sentNotifications.length}</div><div className="text-sm text-slate-600">Notificaciones</div></Card>
        </div>

        <Card className="p-4 mb-6" data-testid="profesores-section">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-indigo-500" /> Profesores de la Institución</h2>
          {profesores.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No hay profesores registrados</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <Button variant="outline" size="sm" onClick={toggleAllProf} data-testid="select-all-prof">
                  {selectedProfesores.length === profesores.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </Button>
                {selectedProfesores.length > 0 && (
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => setNotifType('citatorio')} data-testid="send-citatorio-btn">
                      <AlertTriangle className="w-4 h-4 mr-1" /> Citatorio ({selectedProfesores.length})
                    </Button>
                    <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setNotifType('aviso')} data-testid="send-aviso-btn">
                      <Megaphone className="w-4 h-4 mr-1" /> Aviso ({selectedProfesores.length})
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {profesores.map(prof => (
                  <div key={prof.id} onClick={() => toggleProf(prof.id)}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${selectedProfesores.includes(prof.id) ? 'bg-indigo-50 border-indigo-300' : 'hover:bg-slate-50'}`}
                    data-testid={`prof-item-${prof.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedProfesores.includes(prof.id) ? 'bg-indigo-500 border-indigo-500' : 'border-slate-300'}`}>
                        {selectedProfesores.includes(prof.id) && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                      <div><p className="font-medium text-sm">{prof.name}</p><p className="text-xs text-slate-500">{prof.email}</p></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        {notifType && (
          <Card className={`p-4 mb-6 border-2 ${notifType === 'citatorio' ? 'border-rose-300 bg-rose-50' : 'border-amber-300 bg-amber-50'}`}>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              {notifType === 'citatorio' ? <><AlertTriangle className="w-5 h-5 text-rose-500" /> Enviar Citatorio</> : <><Megaphone className="w-5 h-5 text-amber-500" /> Enviar Aviso</>}
            </h3>
            <p className="text-xs text-slate-600 mb-3">Para: {selectedProfesores.length} profesor(es)</p>
            {notifType === 'citatorio' ? (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Fecha</Label><Input type="date" value={citatorioForm.fecha} onChange={e => setCitatorioForm({...citatorioForm, fecha: e.target.value})} /></div>
                <div><Label>Hora</Label><Input type="time" value={citatorioForm.hora} onChange={e => setCitatorioForm({...citatorioForm, hora: e.target.value})} /></div>
                <div><Label>Lugar</Label><Input value={citatorioForm.lugar} onChange={e => setCitatorioForm({...citatorioForm, lugar: e.target.value})} /></div>
                <div><Label>Motivo</Label><Input value={citatorioForm.motivo} onChange={e => setCitatorioForm({...citatorioForm, motivo: e.target.value})} /></div>
              </div>
            ) : (
              <div><Label>Mensaje</Label><Input value={avisoForm.mensaje} onChange={e => setAvisoForm({...avisoForm, mensaje: e.target.value})} placeholder="Escribe el aviso..." /></div>
            )}
            <div className="flex gap-2 mt-3">
              <Button onClick={notifType === 'citatorio' ? sendCitatorio : sendAviso} disabled={sending} className={notifType === 'citatorio' ? 'bg-rose-500 hover:bg-rose-600' : 'bg-amber-500 hover:bg-amber-600'}>
                <Send className="w-4 h-4 mr-1" /> {sending ? 'Enviando...' : 'Enviar'}
              </Button>
              <Button variant="outline" onClick={() => setNotifType(null)}>Cancelar</Button>
            </div>
          </Card>
        )}

        {sentNotifications.length > 0 && (
          <Card className="p-4">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Bell className="w-5 h-5 text-amber-500" /> Notificaciones Enviadas</h2>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {sentNotifications.map(n => (
                <div key={n.id} className={`p-3 border rounded-lg flex items-start justify-between ${n.notification_type === 'citatorio' ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${n.notification_type === 'citatorio' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                      {n.notification_type === 'citatorio' ? 'Citatorio' : 'Aviso'}
                    </span>
                    <p className="text-sm mt-1"><strong>Para:</strong> {(n.teacher_names || []).join(', ')}</p>
                    {n.motivo && <p className="text-xs text-slate-600">Motivo: {n.motivo}</p>}
                    {n.mensaje && <p className="text-xs text-slate-600">{n.mensaje}</p>}
                    <p className="text-xs text-slate-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleDateString('es-MX') : ''}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteNotif(n.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default JefeDocentesDashboard;
